// App initialization service
// Last updated: 2025-05-25 18:05 - Fixed Cognito User Pool configuration
import { Amplify } from 'aws-amplify';
import { initializeAPI } from './api';
import { initializeDatabase } from './db';
import { initializeSync } from './sync';

/**
 * Initialize the application
 * Sets up database, sync, and API services
 */
export async function initializeApp(): Promise<void> {
  try {
    console.log('Initializing Farm Tasks app...');
    
    // Initialize IndexedDB
    await initializeDatabase();
    console.log('✅ Database initialized');
    
    // Initialize API configuration
    await initializeAPI();
    console.log('✅ API initialized');
    
    // Initialize sync service (but don't start syncing immediately)
    try {
      await initializeSync();
      console.log('✅ Sync service initialized');
    } catch (error) {
      console.warn('⚠️ Sync service initialization failed:', error);
      // Don't fail the entire app if sync fails
    }
    
    // Set up app updates and PWA features (but handle gracefully)
    try {
      setupAppUpdates();
      setupPWAInstall();
      console.log('✅ App features initialized');
    } catch (error) {
      console.warn('⚠️ App features setup failed:', error);
      // Don't fail the entire app if these features fail
    }
    
    console.log('🚀 Farm Tasks app ready!');
  } catch (error) {
    console.error('❌ Failed to initialize app:', error);
    throw error;
  }
}

/**
 * Get app configuration from environment variables
 */
export function getAppConfig() {
  return {
    apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001',
    awsRegion: import.meta.env.VITE_AWS_REGION || 'ap-southeast-2',
    cognitoUserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
    cognitoClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
    s3BucketName: import.meta.env.VITE_S3_BUCKET_NAME || '',
    environment: import.meta.env.MODE || 'development',
    enableMockData: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
  };
}

/**
 * Check if the app is running in standalone PWA mode
 */
export function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
         (window.navigator as any).standalone === true;
}

/**
 * Check if the app is running on a mobile device
 */
export function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Get device capabilities
 */
export function getDeviceCapabilities() {
  return {
    hasCamera: navigator.mediaDevices && 'getUserMedia' in navigator.mediaDevices,
    hasGeolocation: 'geolocation' in navigator,
    hasNotifications: 'Notification' in window,
    hasServiceWorker: 'serviceWorker' in navigator,
    hasIndexedDB: 'indexedDB' in window,
    isOnline: navigator.onLine,
    isStandalone: isStandalone(),
    isMobile: isMobile(),
  };
}

/**
 * Request necessary permissions
 */
export async function requestPermissions(): Promise<{
  camera: boolean;
  microphone: boolean;
  location: boolean;
  notifications: boolean;
}> {
  const permissions = {
    camera: false,
    microphone: false,
    location: false,
    notifications: false,
  };

  try {
    // Camera permission
    if (navigator.mediaDevices && 'getUserMedia' in navigator.mediaDevices) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        permissions.camera = true;
        stream.getTracks().forEach(track => track.stop());
      } catch (err) {
        console.warn('Camera permission denied:', err);
      }
    }

    // Microphone permission
    if (navigator.mediaDevices && 'getUserMedia' in navigator.mediaDevices) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        permissions.microphone = true;
        stream.getTracks().forEach(track => track.stop());
      } catch (err) {
        console.warn('Microphone permission denied:', err);
      }
    }

    // Location permission
    if ('geolocation' in navigator) {
      try {
        await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 5000,
            enableHighAccuracy: false,
          });
        });
        permissions.location = true;
      } catch (err) {
        console.warn('Location permission denied:', err);
      }
    }

    // Notification permission
    if ('Notification' in window) {
      try {
        const permission = await Notification.requestPermission();
        permissions.notifications = permission === 'granted';
      } catch (err) {
        console.warn('Notification permission denied:', err);
      }
    }
  } catch (error) {
    console.error('Error requesting permissions:', error);
  }

  return permissions;
}

/**
 * Handle app updates and service worker updates
 */
export function setupAppUpdates(): void {
  // Listen for app updates only if service worker is available and registered
  if ('serviceWorker' in navigator) {
    try {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Show update notification
        console.log('App updated! Reloading...');
        window.location.reload();
      });
    } catch (error) {
      console.warn('Service worker event listener setup failed:', error);
    }
  }

  // Listen for online/offline events
  window.addEventListener('online', () => {
    console.log('📶 App is online');
    // Trigger sync when coming back online
    window.dispatchEvent(new CustomEvent('app:online'));
  });

  window.addEventListener('offline', () => {
    console.log('📵 App is offline');
    window.dispatchEvent(new CustomEvent('app:offline'));
  });
}

/**
 * Set up PWA install prompt
 */
export function setupPWAInstall(): void {
  let deferredPrompt: any;

  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt = e;
    
    // Show install button/banner
    window.dispatchEvent(new CustomEvent('app:installable', { detail: deferredPrompt }));
  });

  window.addEventListener('appinstalled', () => {
    console.log('🎉 PWA was installed');
    deferredPrompt = null;
  });
}

// Configure Amplify with environment variables
const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || 'ap-southeast-2_WSsgAYRaB',
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '4usa8supomaooe0jfk08qcv0rm',
      region: import.meta.env.VITE_AWS_REGION || 'ap-southeast-2',
      signUpVerificationMethod: 'code' as const,
      loginWith: {
        email: true,
        username: false,
      },
    },
  },
  Storage: {
    S3: {
      bucket: import.meta.env.VITE_S3_BUCKET_NAME || 'farm-tasks-dev-media-b10f2119',
      region: import.meta.env.VITE_AWS_REGION || 'ap-southeast-2',
    },
  },
  API: {
    REST: {
      farmTasksApi: {
        endpoint: import.meta.env.VITE_API_BASE_URL || 'https://api.tasks.waddingtonpark.com.au',
        region: import.meta.env.VITE_AWS_REGION || 'ap-southeast-2',
      },
    },
  },
};

// Configure Amplify
Amplify.configure(amplifyConfig);

export { amplifyConfig };
